import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Key, Check, ChevronDown } from 'lucide-react';
import { assignZipToKernSlot } from '../utils/zoneUtils';
import { cn } from '../utils/cn';
import { inlineLinkClass } from './ui';

interface Props {
    /** All ZIPs in the subscription (ordered). First 3 = Kernzone. */
    allZips: string[];
    /** Called when the user reassigns a Kernzone slot, with the new full zip order. */
    onChange: (newZips: string[]) => void;
    disabled?: boolean;
}

const KERN_SLOT_COUNT = 3;

/**
 * Displays up to 3 Kernzone slots as interactive badge-pickers.
 * Each slot shows the current Kernzone ZIP and opens a dropdown to
 * reassign it to any ZIP from the full pool.
 *
 * Only rendered for FREE tier users with >3 ZIPs.
 */
export const KernzonenField: React.FC<Props> = ({ allZips, onChange, disabled }) => {
    const [openSlot, setOpenSlot] = useState<number | null>(null);

    const kernZips = allZips.slice(0, KERN_SLOT_COUNT);
    const filledSlots = kernZips.length;
    const slots = Array.from({ length: KERN_SLOT_COUNT }, (_, i) => kernZips[i] ?? null);

    const handleSlotClick = (slot: number) => {
        if (disabled) return;
        setOpenSlot(openSlot === slot ? null : slot);
    };

    const handlePickZip = (slot: number, zip: string) => {
        setOpenSlot(null);
        const newZips = assignZipToKernSlot(zip, slot as 0 | 1 | 2, allZips);
        onChange(newZips);
    };

    const handleOverlayClick = () => setOpenSlot(null);

    return (
        <div className="relative rounded-control border border-line bg-card p-3">
            <div className="mb-2 flex items-baseline gap-2">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                    <Key className="h-3.5 w-3.5" aria-hidden="true" /> Kernzone
                </span>
                <span className="text-sm text-muted">volle Inserate-Details</span>
            </div>

            <div className="mb-2 flex flex-wrap gap-2">
                {slots.map((zip, slot) => (
                    <div key={slot} className="relative">
                        <button
                            type="button"
                            className={cn(
                              'inline-flex items-center gap-1 whitespace-nowrap rounded-control border px-2.5 py-1.5 text-sm font-medium',
                              zip
                                ? 'border-brand/40 bg-brand-soft text-on-brand-soft hover:bg-brand-soft/70'
                                : 'cursor-default border-dashed border-line-strong text-muted',
                              openSlot === slot && 'ring-2 ring-brand'
                            )}
                            onClick={() => zip && handleSlotClick(slot)}
                            disabled={disabled || !zip}
                            title={zip ? `Slot ${slot + 1}: ${zip} – klicken zum Ändern` : `Slot ${slot + 1}: leer`}
                        >
                            <span>{zip ?? '—'}</span>
                            {zip && <ChevronDown className="h-3 w-3 opacity-70" aria-hidden="true" />}
                        </button>

                        {openSlot === slot && (
                            <div className="absolute left-0 top-[calc(100%+4px)] z-[1000] max-h-60 min-w-[180px] overflow-y-auto rounded-card border border-line bg-card py-1 shadow-card">
                                <div className="border-b border-line px-3 py-1.5 text-xs font-semibold text-muted">
                                    Slot {slot + 1} belegen mit:
                                </div>
                                <ul>
                                    {allZips.map((candidate) => (
                                        <li key={candidate}>
                                            <button
                                                type="button"
                                                className={cn(
                                                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm text-ink hover:bg-sunken',
                                                  candidate === zip && 'bg-brand-soft font-semibold text-on-brand-soft'
                                                )}
                                                onClick={() => handlePickZip(slot, candidate)}
                                            >
                                                <span>
                                                  {candidate}
                                                  {allZips.indexOf(candidate) < KERN_SLOT_COUNT && candidate !== zip && (
                                                      <span className="ml-1.5 text-xs text-muted">(Kernzone)</span>
                                                  )}
                                                </span>
                                                {candidate === zip && <Check className="h-3.5 w-3.5 text-success-text" aria-hidden="true" />}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {filledSlots < KERN_SLOT_COUNT && (
                <p className="text-sm text-muted">
                    Füge mehr PLZ hinzu, um alle {KERN_SLOT_COUNT} Kernzonen-Slots zu belegen.
                </p>
            )}

            <p className="mt-1 text-sm text-muted">
                Inserate aus anderen PLZ ({allZips.length - KERN_SLOT_COUNT} Randzone-PLZ) werden{' '}
                <strong className="font-semibold text-ink">anonymisiert</strong> angezeigt.{' '}
                <Link to="/dashboard/upgrade" className={inlineLinkClass}>Upgrade für alle Details</Link>
            </p>

            {openSlot !== null && (
                <div className="fixed inset-0 z-[999]" onClick={handleOverlayClick} />
            )}
        </div>
    );
};
